import * as React from "react"

import { cn } from "~/lib/utils"
import { Tip } from '~/components/ui/tip';

/**
 * Initials avatar — used in the sidebar (Eddy) and on the user's own chat
 * bubbles (EDDY's answers use the brand mark, `EddyAvatar`). Stays a tiny initials chip rather than a
 * full image avatar so we don't need to plumb profile-photo URLs through
 * the API yet; matches the look of the mission-control sidebar header.
 */
function initialsFrom(name: string): string {
  const cleaned = name.trim()
  if (!cleaned) return "?"
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

interface InitialsAvatarProps
  extends Omit<React.ComponentProps<"div">, "children"> {
  name: string
  size?: "sm" | "md" | "lg"
  tone?: "primary" | "muted"
}

function InitialsAvatar({
  name,
  size = "md",
  tone = "muted",
  className,
  title,
  ...props
}: InitialsAvatarProps) {
  const initials = initialsFrom(name)
  return (
    <Tip label={title ?? name}>
      <div
        data-slot="avatar"
        aria-label={name}
        className={cn(
          "inline-flex shrink-0 select-none items-center justify-center rounded-md font-semibold tracking-tight",
          size === "sm" && "h-6 w-6 text-[length:var(--text-2xs)]",
          size === "md" && "h-7 w-7 text-xs",
          size === "lg" && "h-9 w-9 text-sm",
          tone === "primary" && "bg-primary text-primary-foreground",
          tone === "muted" && "bg-muted text-foreground/80 ring-1 ring-border/60",
          className,
        )}
        {...props}
      >
        {initials}
      </div>
    </Tip>
  )
}

export { InitialsAvatar, initialsFrom }
